import React, { useRef, useContext } from 'react';
import { Steps, Step } from '../Steps';
import {
    MinusCircleOutlined, FlagOutlined, BranchesOutlined,
    CheckCircleFilled, ArrowRightOutlined
} from '@ant-design/icons';
import Button from '../../common/Button';
import MathJaxHTMLContent from '../../common/MathJaxHTMLContent';
import { Alert } from 'antd';
import DynamicActivity from "./DynamicActivity";
import MediaActivity from "./MediaActivity";
import MediaActivities from "./MediaActivities";
import QuestionActivity from "./QuestionActivity";
import QuestionActivities from "./QuestionActivities";
import WalkthroughActivities from "./WalkthroughActivities";
import SurveyActivity from "./SurveyActivity";
import CustomActivity from "./CustomActivity";
import EndActivity from "./EndActivity";
import TestActivity from "./TestActivity";
import TestResultActivity from "./TestResultActivity";
import ScorcherActivities from "./ScorcherActivities";
import {BrandContext} from "../../common/BrandContext";

export const Timeline = ({ steps, stepIndex, onStepChange }) => (
    <nav aria-label="Step timeline navigation" className="flexColumn">
        <div className="flexNoWrap" style={{ overflowX: 'auto' }} role="list">
            <Steps
                current={stepIndex}
                labelPlacement="vertical"
                onChange={onStepChange}
                className="js-pw-timeline ch-trail-steps"
                size="small"
                aria-label="Progress through task steps"
            >
                {steps.map((step, index) => {
                    let status = 'wait';
                    if(step.endStep) {
                        status = 'end';
                    } else if (step.complete) {
                        status = 'finish';
                    } else if(step.skipStep) {
                        status = 'skip';
                    }
                    if ((step.currentStep || index === stepIndex) && !step.endStep) status = 'process';

                    const disabled = index > stepIndex && !step.complete && !step.skipStep;

                    let icon;
                    const stepColor = disabled ? '#000' : '#fff';
                    if (step.endStep) {
                        icon = <FlagOutlined aria-hidden="true" style={{color: stepColor}}/>;
                    } else if (step.branchStep) {
                        icon = <BranchesOutlined aria-hidden="true" style={{ color: stepColor}}/>;
                    } else if (index == stepIndex) {
                        icon = <ArrowRightOutlined aria-hidden="true" style={{ color: stepColor}}/>
                    } else if (step.complete) {
                        icon = <CheckCircleFilled aria-hidden="true" style={{ color: stepColor}} />;
                    } else if (step.skipStep) {
                        icon = <MinusCircleOutlined aria-hidden="true" style={{ color: stepColor}}/>;
                    }

                    return (
                        <Step
                            key={step.stepId}
                            title={step.stepName}
                            status={status}
                            aria-label={step.stepName}
                            icon={icon}
                            data={step}
                            disabled={disabled}
                            className={`ch-trail-step${index === stepIndex ? ' active' : ''}`}
                            aria-current={index === stepIndex ? 'step' : undefined}
                        />
                    );
                })}
            </Steps>
        </div>
    </nav>
);

export const NavigationButtons = ({
                                      pathwayUser,
                                      stepIndex,
                                      step,
                                      steps,
                                      onStepChange,
                                      handleBack,
                                      handleSkipStep,
                                      continueCurrentStep,
                                      continueStepDisabled,
                                      skipStepDisabled
                                  }) => {
    const brandConfig = useContext(BrandContext);

    return (
        <div className="flexSpaceBetween flexAlignCentre ch-nav-buttons" role="region" aria-labelledby="step-title">
            <h2 id="step-title" className="ch-nav-title" tabIndex="-1">{pathwayUser.pathwayAssignmentName}</h2>
            <div className="flexInline smallGap" role="group" aria-label="Step navigation controls">
                {(step.complete || pathwayUser.complete) ? (
                    <>
                        <Button
                            type="primary"
                            onClick={() => onStepChange(stepIndex - 1)}
                            disabled={stepIndex <= 0}
                            aria-label="Go to previous step"
                        >
                            Previous
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => onStepChange(stepIndex + 1)}
                            disabled={stepIndex >= steps.length - 1}
                            aria-label="Go to next step"
                        >
                            Next
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            type="primary"
                            onClick={handleSkipStep}
                            disabled={skipStepDisabled}
                            aria-label="Skip this step"
                        >
                            Skip Step
                        </Button>
                        <Button
                            type="primary"
                            onClick={continueCurrentStep}
                            disabled={continueStepDisabled}
                            aria-label="Continue to next activity"
                        >
                            {step?.moreMedia
                                ? `Next ${brandConfig?.[step.mediaType?.toLowerCase() + 'Name']?.toLowerCase() ?? 'item'}`
                                : 'Continue'}
                        </Button>
                    </>
                )}
                <Button
                    type="primary"
                    onClick={handleBack}
                    aria-label="Return to previous screen"
                >
                    Back
                </Button>
            </div>
        </div>
    );
};

const activityComponents = {
    DynamicActivity,
    MediaActivity,
    MediaActivities,
    QuestionActivity,
    QuestionActivities,
    WalkthroughActivities,
    ScorcherActivities,
    SurveyActivity,
    CustomActivity,
    EndActivity,
    TestActivity,
    TestResultActivity
};

export const ContentArea = ({
                                step,
                                stepUserResult,
                                stepActivities,
                                stepActivity,
                                pathwayUser,
                                continueCurrentStep,
                                setStepUserResult,
                                handleBack
                            }) => {

    if (!step || !pathwayUser) {
        return <Alert role="alert" showIcon={true} message="Invalid step" type="error" />;
    }

    const currentComponent = (step.complete || pathwayUser.complete)
        ? step.resultComponent
        : step.tabComponent;

    if (!currentComponent) {
        return <Alert showIcon={true} message="Invalid activity" type="error"/>;
    }

    const ActivityComponent = activityComponents[currentComponent];
    if (!ActivityComponent) {
        return <Alert showIcon={true} message="Invalid activity" type="error"/>;
    }

    return (
        <section
            className="ch-trail-activity"
            aria-live="polite"
            aria-labelledby="activity-title"
            style={{ border: '1px solid var(--border-colour)', borderRadius: 'var(--container-radius)', padding: 'var(--spacing-unit)' }}
        >
            {!step.endStep && (
                <>
                    <h3 id="activity-title" className="ch-trail-activity-title" tabIndex="-1">
                        {step.stepName}{' '}
                        {!pathwayUser.complete &&
                            stepUserResult?.repeatCount !== undefined &&
                            stepUserResult?.repeatTotal > 1 && (
                                <span>
                                    ({stepUserResult.repeatCount + 1} of {stepUserResult.repeatTotal})
                                </span>
                            )}
                    </h3>
                    <div>
                        <MathJaxHTMLContent html={step.stepContentName} />
                    </div>
                </>
            )}
            <div
                className="ch-trail-activity-content js-pw-card-blk active"
                role="region"
                aria-label={`Activity content for ${step.stepName}`}
            >
                <ActivityComponent
                    className="pw-card"
                    stepActivity={stepActivity}
                    stepActivities={stepActivities}
                    pathwayUser={pathwayUser}
                    step={step}
                    stepUserResult={stepUserResult}
                    continueCurrentStep={continueCurrentStep}
                    setStepUserResult={setStepUserResult}
                    handleBack={handleBack}
                />
            </div>
        </section>
    );
};
